import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { parseISO } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { detailDateTime, markerDateTime } from '../../dateFormat'
import type { UseEventDetailResult } from '../../hooks/useEventDetail'
import { DetailRow } from './DetailRow'

type Props = {
  event: CalendarEvent
  isMarker: boolean
  /** DUE row label: "Due" for an activity, "Next Action"/"Expected Close" for a marker. */
  dueLabel: string
  detail: UseEventDetailResult
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

/**
 * The popover body: the fact rows (each removed from the DOM when empty, Q3),
 * the description block, and the hydration loading / load-error states.
 */
export function PopoverBody({ event, isMarker, dueLabel, detail }: Props) {
  const theme = useTheme()

  return (
    <Box
      sx={{
        padding: '16px 24px 22px',
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      {!isMarker && (
        <DetailRow
          icon="time"
          label="Start"
          value={detailDateTime(parseISO(event.start))}
        />
      )}
      <DetailRow
        icon="due"
        label={dueLabel}
        value={
          isMarker
            ? markerDateTime(parseISO(event.start))
            : detailDateTime(parseISO(event.end))
        }
      />
      {/* Hydrated rows (contact/value/owner) reserve their place with a skeleton
          while loading, so they fill in rather than popping in and pushing the
          description down. After loading, an empty one collapses (Q3). */}
      {isMarker && (detail.isLoading || detail.data?.totalValue != null) && (
        <DetailRow
          icon="value"
          label="Total Value"
          loading={detail.isLoading}
          value={
            detail.data?.totalValue != null
              ? currency.format(detail.data.totalValue)
              : undefined
          }
        />
      )}
      {!isMarker && (detail.isLoading || detail.data?.primaryContactName) && (
        <DetailRow
          icon="contact"
          label="Primary Contact"
          loading={detail.isLoading}
          value={detail.data?.primaryContactName}
        />
      )}
      {(detail.isLoading || detail.data?.owner) && (
        <DetailRow
          icon="owner"
          label="Owner"
          loading={detail.isLoading}
          value={detail.data?.owner}
        />
      )}
      {!isMarker && event.description && (
        <Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '.05em',
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
              mb: '4px',
            }}
          >
            Description
          </Typography>
          <Box
            sx={{
              fontSize: '13.5px',
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.grey[50],
              borderRadius: '6px',
              padding: '10px 12px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {event.description}
          </Box>
        </Box>
      )}
      {detail.loadError && (
        <Typography
          data-test-id="event-detail-load-error"
          sx={{ fontSize: '13px', color: theme.palette.text.secondary }}
        >
          We couldn&apos;t load this record&apos;s details.
        </Typography>
      )}
    </Box>
  )
}
